import { useComponentState } from "@faedev/dynamic-values";
import { composeClassNames } from "@faedev/editor-utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Button, Dropdown, Nav, NavDropdown, Spinner } from "react-bootstrap";
import { useCookies } from "react-cookie";
import ModalLogin from "src/components/ModalLogin";
import ModalRegister from "src/components/ModalRegister";
import ModalResetPassword from "src/components/ModalResetPassword";
import MqGteDesktop from "src/components/MqGteDesktop";
import MqLteTablet from "src/components/MqLteTablet";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import getLink from "src/functions/getLink";
import useLoggedInUser from "src/functions/useLoggedInUser";
import validateAuthToken from "src/functions/validateAuthToken";
import Profile from "src/icons/Profile";
import Small_video from "src/icons/Small-video";
import { sButton } from "src/styles/sButton.module.scss";
import { sDropdownAccount } from "src/styles/sDropdownAccount.module.scss";
import { sDropdownItem } from "src/styles/sDropdownItem.module.scss";
import { sDropdownMembers } from "src/styles/sDropdownMembers.module.scss";
import { sMenuMain } from "src/styles/sMenuMain.module.scss";
import { sMenuMainLink } from "src/styles/sMenuMainLink.module.scss";
import { sMenuMainLinkCircle } from "src/styles/sMenuMainLinkCircle.module.scss";
import FlagCz from "src/icons/Flag-cz";
import FlagSk from "src/icons/Flag-sk";
import FlagGb from "src/icons/Flag-gb";

type Props = {
  setAlertResetPassword?: (setAlertResetPassword: boolean) => void;
};

function MenuMain({ setAlertResetPassword }: Props) {
  const { locale: lang, asPath, push, pathname } = useRouter();
  var [cookies, setCookie, removeCookie] = useCookies(null);
  var openModalLogin = useComponentState(false);
  var openModalRegister = useComponentState(false);
  var openModalResetPassword = useComponentState(false);
  var userResult = useLoggedInUser();
  const [activeSubscription, setActiveSubscription] = useState(null);

  useEffect(() => {
    userResult.result &&
      fetch(`${constants.Paths.apiUrl}/clients/me`, {
        method: "GET",
        headers: validateAuthToken(getCookieByName(`auth-token`)),
      }).then(async (res) => {
        if (res.ok) {
          const result = await res.json();
          const subscriptionTill = result.subscription_till;
          const today = new Date();

          if (new Date(subscriptionTill) >= today) {
            setActiveSubscription(result);
          }
        }
      });
  }, [userResult.result]);

  return (
    <Nav className={sMenuMain}>
      <Link href={getLink(lang, null, null)} passHref>
        <Nav.Link
          active={pathname !== `/` ? false : true}
          className={composeClassNames([sMenuMainLink, "item-home"])}
        >
          <Translate tKey={`Home`} />
        </Nav.Link>
      </Link>
      <Link
        href={getLink(
          lang,
          activeSubscription
            ? `classes?subscription=true`
            : `classes?paid=true`,
          null
        )}
        passHref
      >
        <Nav.Link
          active={pathname !== constants.Paths.pageClasses ? false : true}
          className={composeClassNames([sMenuMainLink, "item-content"])}
        >
          <Translate tKey={`Series and classes`} />
        </Nav.Link>
      </Link>
      <Link href={getLink(lang, `profiles`, null)} passHref>
        <Nav.Link
          active={pathname !== constants.Paths.pageProfiles ? false : true}
          className={composeClassNames([sMenuMainLink, "item-profiles"])}
        >
          <Translate tKey={`Profiles`} />
        </Nav.Link>
      </Link>
      {constants.Show.pageBenefits && (
        <Link href={getLink(lang, `benefits`, null)} passHref>
          <Nav.Link
            active={pathname !== constants.Paths.pageBenefits ? false : true}
            className={composeClassNames([sMenuMainLink, "item-benefits"])}
          >
            <Translate tKey={`Benefits`} />
          </Nav.Link>
        </Link>
      )}
      <Link href={getLink(lang, `prices`, null)} passHref>
        <Nav.Link
          active={pathname !== `/prices` ? false : true}
          className={composeClassNames([sMenuMainLink, "item-prices"])}
        >
          <Translate tKey={`Prices`} />
        </Nav.Link>
      </Link>
      <NavDropdown
        id={`membershipMenu`}
        className={composeClassNames([sDropdownMembers, "item-members"])}
        title={
          <Button
            className={composeClassNames([sButton])}
            size="sm"
            type="button"
            variant={
              pathname !== `/members` &&
              pathname !== `/companies` &&
              pathname !== `/instructors`
                ? `dark`
                : `light`
            }
          >
            <Translate tKey={`Try membership`} />
          </Button>
        }
      >
        <Link href={getLink(lang, `companies`)} passHref>
          <Dropdown.Item className={sDropdownItem}>
            <Translate tKey={`For companies`} />
          </Dropdown.Item>
        </Link>
        <Dropdown.Divider />
        <Link href={getLink(lang, `members`)} passHref>
          <Dropdown.Item className={sDropdownItem}>
            <Translate tKey={`For users`} />
          </Dropdown.Item>
        </Link>
        <Dropdown.Divider />
        <Link href={getLink(lang, `instructors`)} passHref>
          <Dropdown.Item className={sDropdownItem}>
            <Translate tKey={`For instructors`} />
          </Dropdown.Item>
        </Link>
      </NavDropdown>
      <NavDropdown
        id={`langMenu`}
        className={composeClassNames([sMenuMainLink, "item-lang"])}
        title={
          <>
            <label>
              <Translate tKey={`Language`} />:
            </label>
            {(() => {
              switch (lang) {
                case `cs`:
                  return <FlagCz />;
                case `sk`:
                  return <FlagSk />;
                case `en`:
                  return <FlagGb />;
                default:
                  return <FlagGb />;
              }
            })()}
          </>
        }
      >
        <Link href={asPath} passHref={true} locale="cs">
          <NavDropdown.Item
            onClick={() => {
              setCookie("NEXT_LOCALE", "cs");
            }}
            className={sDropdownItem}
          >
            <FlagCz />
            <Translate tKey={`czech`} />
          </NavDropdown.Item>
        </Link>
        <Link href={asPath} passHref={true} locale="sk">
          <NavDropdown.Item
            onClick={() => {
              setCookie("NEXT_LOCALE", "sk");
            }}
            className={sDropdownItem}
          >
            <FlagSk />
            <Translate tKey={`slovak`} />
          </NavDropdown.Item>
        </Link>
        <Link href={asPath} passHref={true} locale="en">
          <NavDropdown.Item
            onClick={() => {
              setCookie("NEXT_LOCALE", "en");
            }}
            className={sDropdownItem}
          >
            <FlagGb />
            <Translate tKey={`english`} />
          </NavDropdown.Item>
        </Link>
      </NavDropdown>
      {constants.Show.contentAdd ? (
        <MqGteDesktop>
          <Nav.Link
            className={composeClassNames([
              sMenuMainLinkCircle,
              "item-add-video",
            ])}
          >
            <Small_video size={20} />
          </Nav.Link>
        </MqGteDesktop>
      ) : null}
      {constants.Show.contentAdd ? (
        <MqLteTablet>
          <Nav.Link
            className={composeClassNames([sMenuMainLink, "item-add-content"])}
          >
            <Translate tKey={`Add content`} />
          </Nav.Link>
        </MqLteTablet>
      ) : null}
      {constants.Show.accountSign ? (
        <>
          {userResult.result ? (
            <NavDropdown
              id={`accountMenu`}
              className={composeClassNames([
                sDropdownAccount,
                "authorized",
                "item-account",
              ])}
              title={
                <div>
                  <MqGteDesktop>
                    <div className={`${sMenuMainLinkCircle} light`}>
                      <Profile size={20} />
                    </div>
                  </MqGteDesktop>
                  <MqLteTablet>
                    <Translate tKey={`User account`} />
                  </MqLteTablet>
                </div>
              }
            >
              <Link href={getLink(lang, `account`)} passHref>
                <Dropdown.Item className={sDropdownItem}>
                  <Translate tKey={`Profile`} />
                </Dropdown.Item>
              </Link>
              <Dropdown.Divider />
              <Dropdown.Item
                className={sDropdownItem}
                onSelect={function () {
                  removeCookie(`auth-token`, constants.CookiesOptions);
                  removeCookie(`orderId`, constants.CookiesOptions);
                  window.location.href.indexOf("cart") > -1 ||
                  window.location.href.indexOf("account") > -1
                    ? (location.href = "/")
                    : location.reload();
                }}
              >
                <Translate tKey={`Logout`} />
              </Dropdown.Item>
            </NavDropdown>
          ) : userResult.loading ? (
            <Spinner size={"sm"} animation="border" />
          ) : (
            <>
              <NavDropdown
                id={`accountMenu`}
                className={composeClassNames([
                  sDropdownAccount,
                  "unauthorized",
                  "item-account",
                ])}
                title={
                  <div>
                    <MqGteDesktop>
                      <div className={`${sMenuMainLinkCircle} light`}>
                        <Profile size={20} />
                      </div>
                    </MqGteDesktop>
                    <MqLteTablet>
                      <Translate tKey={`User account`} />
                    </MqLteTablet>
                  </div>
                }
              >
                <Dropdown.Item
                  className={sDropdownItem}
                  onSelect={function () {
                    return openModalLogin.change(true);
                  }}
                >
                  <Translate tKey={`Sign in`} />
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  className={sDropdownItem}
                  onSelect={function () {
                    return openModalRegister.change(true);
                  }}
                >
                  <Translate tKey={`Sign up`} />
                </Dropdown.Item>
              </NavDropdown>
              <ModalLogin
                openModalLogin={openModalLogin}
                openModalRegister={openModalRegister}
                openModalResetPassword={openModalResetPassword}
                key={`ModalLogin`}
              />
              <ModalRegister
                openModalLogin={openModalLogin}
                openModalRegister={openModalRegister}
                key={`ModalRegister`}
              />
              <ModalResetPassword
                openModalResetPassword={openModalResetPassword}
                key={`ModalResetPassword`}
                setAlertResetPassword={setAlertResetPassword}
              />
            </>
          )}
        </>
      ) : null}
    </Nav>
  );
}

export default MenuMain;
